import { useMutation, useQuery, useQueryClient } from 'react-query';

import { UsersApi } from '../../api/client';
import { DRAWER_TABS } from '../../constants';
import { DeleteUserData, InviteUserData } from '../../models';
import { AdminPanelUsers, User } from '../../shared/types';
import View from './View';

const Container: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: users, isLoading: isLoadingUsers } = useQuery<AdminPanelUsers>(
    'USERS',
    UsersApi.fetchAllUsers,
    {
      onError: (err) => {
        console.log(err);
      },
    }
  );

  const handelEditUser = (
    updatedUser: User,
    selectedDrawerTab: DRAWER_TABS
  ) => {
    if (selectedDrawerTab === DRAWER_TABS.PATIENTS) {
      editPatient.mutate(updatedUser);
    }
    if (selectedDrawerTab === DRAWER_TABS.ADMINS) {
      editAdmin.mutate(updatedUser);
    }
    if (selectedDrawerTab === DRAWER_TABS.CARE_PROVIDERS) {
      editCareProvider.mutate(updatedUser);
    }
  };

  const handelRemoveUser = (userId: string, selectedDrawerTab: DRAWER_TABS) => {
    if (selectedDrawerTab === DRAWER_TABS.ADMINS) {
      deleteAdmin.mutate({ userId });
    }
    if (selectedDrawerTab === DRAWER_TABS.CARE_PROVIDERS) {
      deleteCareProvider.mutate({ userId });
    }
  };

  const handelInviteUser = (email: string, selectedDrawerTab: DRAWER_TABS) => {
    if (selectedDrawerTab === DRAWER_TABS.ADMINS) {
      inviteAdmin.mutate({ email });
    }
    if (selectedDrawerTab === DRAWER_TABS.CARE_PROVIDERS) {
      inviteCareProvider.mutate({ email });
    }
    if (selectedDrawerTab === DRAWER_TABS.PATIENTS) {
      invitePatient.mutate({ email });
    }
  };

  const editPatient = useMutation(
    (updatedUser: User) => UsersApi.editPatient(updatedUser),
    {
      onMutate: async (updatedUser) => {
        await queryClient.cancelQueries('USERS');
        const previousUsers = queryClient.getQueryData<AdminPanelUsers>(
          'USERS'
        );
        const newPatients = previousUsers?.patients.filter(
          (patient) => patient.id !== updatedUser.id
        );
        if (previousUsers && newPatients) {
          newPatients?.unshift(updatedUser);
          queryClient.setQueryData<AdminPanelUsers>('USERS', {
            ...previousUsers,
            patients: newPatients,
          });
        }
        return { previousUsers };
      },
      onError: (err, _, context: { previousUsers: AdminPanelUsers }) => {
        console.log(err);
        queryClient.setQueryData('USERS', context?.previousUsers);
      },
      onSettled: () => {
        queryClient.invalidateQueries('USERS');
      },
    }
  );

  const editAdmin = useMutation(
    (updatedUser: User) => UsersApi.editAdmin(updatedUser),
    {
      onMutate: async (updatedUser) => {
        await queryClient.cancelQueries('USERS');
        const previousUsers = queryClient.getQueryData<AdminPanelUsers>(
          'USERS'
        );
        const newAdmins = previousUsers?.admins.filter(
          (admin) => admin.id !== updatedUser.id
        );
        if (previousUsers && newAdmins) {
          newAdmins?.unshift(updatedUser);
          queryClient.setQueryData<AdminPanelUsers>('USERS', {
            ...previousUsers,
            admins: newAdmins,
          });
        }
        return { previousUsers };
      },
      onError: (err, _, context: { previousUsers: AdminPanelUsers }) => {
        console.log(err);
        queryClient.setQueryData('USERS', context?.previousUsers);
      },
      onSettled: () => {
        queryClient.invalidateQueries('USERS');
      },
    }
  );

  const editCareProvider = useMutation(
    (updatedUser: User) => UsersApi.editCareProvider(updatedUser),
    {
      onMutate: async (updatedUser) => {
        await queryClient.cancelQueries('USERS');
        const previousUsers = queryClient.getQueryData<AdminPanelUsers>(
          'USERS'
        );
        const newProviders = previousUsers?.careProviders.filter(
          (provider) => provider.id !== updatedUser.id
        );
        if (previousUsers && newProviders) {
          newProviders?.unshift(updatedUser);
          queryClient.setQueryData<AdminPanelUsers>('USERS', {
            ...previousUsers,
            careProviders: newProviders,
          });
        }
        return { previousUsers };
      },
      onError: (err, _, context: { previousUsers: AdminPanelUsers }) => {
        console.log(err);
        queryClient.setQueryData('USERS', context?.previousUsers);
      },
      onSettled: () => {
        queryClient.invalidateQueries('USERS');
      },
    }
  );

  const deleteCareProvider = useMutation(
    (data: DeleteUserData) => UsersApi.deleteCareProvider(data),
    {
      onMutate: async ({ userId }) => {
        await queryClient.cancelQueries('USERS');
        const previousUsers = queryClient.getQueryData<AdminPanelUsers>(
          'USERS'
        );
        const newProviders = previousUsers?.careProviders.filter(
          (provider) => provider.id !== userId
        );
        if (previousUsers && newProviders) {
          queryClient.setQueryData<AdminPanelUsers>('USERS', {
            ...previousUsers,
            careProviders: newProviders,
          });
        }
        return { previousUsers };
      },
      onError: (err, _, context: { previousUsers: AdminPanelUsers }) => {
        console.log(err);
        queryClient.setQueryData('USERS', context?.previousUsers);
      },
      onSettled: () => {
        queryClient.invalidateQueries('USERS');
      },
    }
  );

  const deleteAdmin = useMutation(
    (data: DeleteUserData) => UsersApi.deleteAdmin(data),
    {
      onMutate: async ({ userId }) => {
        await queryClient.cancelQueries('USERS');
        const previousUsers = queryClient.getQueryData<AdminPanelUsers>(
          'USERS'
        );
        const newAdmins = previousUsers?.admins.filter(
          (admin) => admin.id !== userId
        );
        if (previousUsers && newAdmins) {
          queryClient.setQueryData<AdminPanelUsers>('USERS', {
            ...previousUsers,
            admins: newAdmins,
          });
        }
        return { previousUsers };
      },
      onError: (err, _, context: { previousUsers: AdminPanelUsers }) => {
        console.log(err);
        queryClient.setQueryData('USERS', context?.previousUsers);
      },
      onSettled: () => {
        queryClient.invalidateQueries('USERS');
      },
    }
  );

  const invitePatient = useMutation(
    (data: InviteUserData) => UsersApi.invitePatient(data),
    {
      onSuccess: () => {
        // TODO any changes to render on view?
      },
      onError: (err) => {
        console.log(err);
      },
    }
  );

  const inviteAdmin = useMutation(
    (data: InviteUserData) => UsersApi.inviteAdmin(data),
    {
      onSuccess: () => {
        // TODO any changes to render on view?
      },
      onError: (err) => {
        console.log(err);
      },
    }
  );

  const inviteCareProvider = useMutation(
    (data: InviteUserData) => UsersApi.inviteCareProvider(data),
    {
      onSuccess: () => {
        // TODO any changes to render on view?
      },
      onError: (err) => {
        console.log(err);
      },
    }
  );

  const isLoadingData = () => {
    return (
      isLoadingUsers ||
      inviteCareProvider.isLoading ||
      inviteAdmin.isLoading ||
      invitePatient.isLoading
    );
  };

  return (
    <View
      isLoading={isLoadingData()}
      patients={users?.patients}
      admins={users?.admins}
      careProviders={users?.careProviders}
      editUser={handelEditUser}
      removeUser={handelRemoveUser}
      inviteUser={handelInviteUser}
    />
  );
};

export default Container;
